// FamilySelector.js - Fixed accordion height issue
import React, { useState, useEffect, useRef } from "react"
import { AiOutlinePlus } from "react-icons/ai"
import { fetchProductStructure } from "./api"
import { getTranslations } from "./components/translations"
import { debug } from "./debug"

// For direct API calls
const API_BASE_URL = "https://dieffebi-demo.ayea.it/api"

const FamilySelector = ({
  products,
  selectedFamily,
  onFamilyChange,
  selectedProduct,
  language = "it", // Default to Italian if not provided
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState({})
  const [productStructure, setProductStructure] = useState({})
  const [categoryData, setCategoryData] = useState({}) // Store full category data including translations
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [loading, setLoading] = useState(false)
  const [contentHeight, setContentHeight] = useState(0)
  const [categoryHeights, setCategoryHeights] = useState({}) // Track height for each category
  const dropdownRef = useRef(null)
  const contentRef = useRef(null)
  const categoryRefs = useRef({})
  const categoryContentRefs = useRef({}) // Refs for category content
  const headerClickRef = useRef(false)

  // Get translations based on the language prop
  const translations = getTranslations(language)

  // Load product structure on component mount
  useEffect(() => {
    loadProductStructure()
  }, [])

  // Better height calculation when content changes
  useEffect(() => {
    const updateHeight = () => {
      if (isDropdownOpen && contentRef.current) {
        // Add a small buffer to ensure everything fits
        const calculatedHeight = contentRef.current.scrollHeight + 20
        setContentHeight(calculatedHeight)
      } else {
        setContentHeight(0)
      }
    }

    // Initial update
    updateHeight()

    // Add a small delay to ensure all elements have rendered properly
    const timeoutId = setTimeout(updateHeight, 50)

    return () => clearTimeout(timeoutId)
  }, [isDropdownOpen, productStructure, expandedCategories])

  // Add a specific effect to recalculate height when category expands
  useEffect(() => {
    if (isDropdownOpen && contentRef.current) {
      // Force a recalculation after a slight delay to account for animations
      const timeoutId = setTimeout(() => {
        const calculatedHeight = contentRef.current.scrollHeight + 20
        setContentHeight(calculatedHeight)
      }, 310) // Just after the 300ms transition

      return () => clearTimeout(timeoutId)
    }
  }, [expandedCategories, isDropdownOpen])

  // Calculate heights for individual category accordions
  useEffect(() => {
    const newCategoryHeights = {}

    Object.keys(expandedCategories).forEach((category) => {
      const contentRef = categoryContentRefs.current[category]
      if (contentRef) {
        if (expandedCategories[category]) {
          // Calculate actual content height when expanded
          newCategoryHeights[category] = contentRef.scrollHeight
        } else {
          newCategoryHeights[category] = 0
        }
      }
    })

    setCategoryHeights(newCategoryHeights)
  }, [expandedCategories, productStructure])

  // Update selected category when selected family changes
  useEffect(() => {
    if (selectedFamily) {
      // Find which category contains this family
      for (const category in productStructure) {
        if (productStructure[category]?.includes(selectedFamily)) {
          setSelectedCategory(category)

          // Ensure only this category is expanded, close others
          const newExpandedState = {}
          Object.keys(expandedCategories).forEach((cat) => {
            newExpandedState[cat] = cat === category
          })
          setExpandedCategories(newExpandedState)
          break
        }
      }
    }
  }, [selectedFamily, productStructure])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !headerClickRef.current &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false)
      }
      headerClickRef.current = false
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Load product structure
  const loadProductStructure = async () => {
    setLoading(true)
    try {
      // Get product structure and categories
      const structure = await fetchProductStructure()
      setProductStructure(structure)

      // Fetch category data from products.json
      const categoryNames = Object.keys(structure)
      setCategories(categoryNames)

      // Get the full categories data from the API
      let categoriesData = {}

      try {
        // First try to get data from products prop
        if (products && products.categories) {
          categoriesData = products.categories
          debug.app("Categories loaded from products prop:", categoriesData)
        }
        // If not available, fetch from API
        else {
          const fullProductsData = await fetch(
            `${API_BASE_URL || ""}/products.php`
          ).then((response) => response.json())

          if (fullProductsData && fullProductsData.categories) {
            categoriesData = fullProductsData.categories
            debug.app("Categories loaded from API:", categoriesData)
          }
        }
      } catch (err) {
        debug.warn.app("Could not load full categories data:", err)
      }

      // Process category data with translations
      const catData = {}
      categoryNames.forEach((categoryName) => {
        if (categoriesData[categoryName]) {
          catData[categoryName] = categoriesData[categoryName]
        } else {
          // If no match, create simple object with just the name
          catData[categoryName] = { name: categoryName }
        }
      })

      setCategoryData(catData)
      debug.app("Processed category data:", catData)

      // Initialize expanded state for categories
      const initialExpandedCategories = {}
      categoryNames.forEach((category) => {
        initialExpandedCategories[category] = false
      })

      // If we have a selected family, find its category
      if (selectedFamily) {
        for (const category of categoryNames) {
          if (structure[category]?.includes(selectedFamily)) {
            setSelectedCategory(category)
            // Auto-expand only this category
            categoryNames.forEach((cat) => {
              initialExpandedCategories[cat] = cat === category
            })
            break
          }
        }
      }
      // Otherwise, select the first category
      else if (categoryNames.length > 0) {
        setSelectedCategory(categoryNames[0])
      }

      setExpandedCategories(initialExpandedCategories)
    } catch (error) {
      debug.warn.app("Failed to load product structure:", error)
    } finally {
      setLoading(false)
    }
  }

  // Get localized category name based on language and category data
  const getCategoryName = (categoryKey) => {
    const catData = categoryData[categoryKey]

    if (catData && catData.lang && catData.lang[language]) {
      return catData.lang[language]
    }

    return categoryKey // Fallback to category key if no translation
  }

  // Toggle category expansion - ensure only one is open at a time
  const toggleCategory = (e, category) => {
    e.stopPropagation()
    e.preventDefault() // Prevent any default behavior

    setExpandedCategories((prev) => {
      const newState = {}
      // Copy current state to new state
      Object.keys(prev).forEach((cat) => {
        newState[cat] = cat === category ? !prev[cat] : false
      })
      return newState
    })
  }

  // Toggle the dropdown open/close
  const toggleDropdown = (e) => {
    e.stopPropagation()
    headerClickRef.current = true
    setIsDropdownOpen((prev) => !prev)
  }

  // Handle family selection - IMPORTANT: Only change family, not product
  const handleFamilySelect = (e, category, family) => {
    e.stopPropagation()

    // Only update the family, let FamilyAccordion handle product selection
    setSelectedCategory(category)
    onFamilyChange(family)
    setIsDropdownOpen(false)
  }

  return (
    <div className="ay-w-full ay-py-2 ay-border-b ay-border-gray-200">
      <div
        className="ay-group ay-gap-x-4 ay-flex ay-justify-between ay-cursor-pointer"
        onClick={toggleDropdown}
      >
        {/* Header section with aligned title and selected family */}
        <div className="ay-flex-grow">
          <div className="ay-flex ay-items-center ay-justify-between">
            <p className="ay-capitalize">
              {translations.family}:{" "}
              {selectedFamily ? (
                <span className="ay-font-medium ay-text-black/40 ay-ml-1 ay-uppercase">
                  {selectedFamily}
                </span>
              ) : (
                <span className="ay-font-medium ay-text-black/40 ay-ml-1 ay-uppercase">
                  {translations.selectFamily}
                </span>
              )}
            </p>
            <div className="ay-flex ay-items-center ay-justify-center ay-w-6 ay-h-6">
              <span
                className={`ay-transition-transform ay-duration-300 ay-ease-in-out ${
                  isDropdownOpen ? "ay-rotate-45" : "ay-rotate-0"
                }`}
              >
                <AiOutlinePlus />
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Accordion Content with animation and fixed overflow */}
      <div
        ref={contentRef}
        className="ay-overflow-hidden ay-transition-all ay-duration-300 ay-ease-in-out"
        style={{
          maxHeight: `${contentHeight}px`,
          opacity: isDropdownOpen ? 1 : 0,
        }}
      >
        <div
          className="ay-mt-4 ay-transform ay-transition-transform ay-duration-300 ay-ease-in-out"
          style={{
            transform: isDropdownOpen ? "translateY(0)" : "translateY(-10px)",
          }}
        >
          {/* Dropdown */}
          <div ref={dropdownRef} className="ay-w-full">
            {loading ? (
              <div className="ay-px-4 ay-py-3 ay-text-gray-500 ay-text-center">
                {translations.loadingProducts}
              </div>
            ) : categories.length > 0 ? (
              categories.map((category) => (
                <div
                  key={category}
                  className="ay-border-b ay-border-gray-200 ay-last:ay-border-0"
                  ref={(el) => (categoryRefs.current[category] = el)}
                >
                  {/* Category header */}
                  <div
                    onClick={(e) => toggleCategory(e, category)}
                    className="ay-flex ay-justify-between ay-items-center ay-px-4 ay-py-2 ay-cursor-pointer ay-hover:ay-bg-gray-50"
                  >
                    <span className="ay-font-medium">
                      {getCategoryName(category)}
                    </span>
                    <div className="ay-flex ay-items-center ay-justify-center ay-w-5 ay-h-5">
                      <span
                        className={`ay-transition-transform ay-text-xs ay-duration-300 ay-ease-in-out ${
                          expandedCategories[category]
                            ? "ay-rotate-45"
                            : "ay-rotate-0"
                        }`}
                      >
                        <AiOutlinePlus />
                      </span>
                    </div>
                  </div>

                  {/* FIXED: Families in this category - with dynamic height control */}
                  <div
                    className="ay-overflow-hidden ay-transition-all ay-duration-300 ay-ease-in-out"
                    style={{
                      maxHeight: `${categoryHeights[category] || 0}px`,
                      opacity: expandedCategories[category] ? 1 : 0,
                    }}
                  >
                    <div
                      ref={(el) => (categoryContentRefs.current[category] = el)}
                      className="ay-pl-8 ay-pr-4 ay-pb-2 ay-transform ay-transition-transform ay-duration-300"
                      style={{
                        transform: expandedCategories[category]
                          ? "translateY(0)"
                          : "translateY(-10px)",
                      }}
                    >
                      {productStructure[category]?.length > 0 ? (
                        productStructure[category].map((family) => (
                          <div
                            key={family}
                            onClick={(e) =>
                              handleFamilySelect(e, category, family)
                            }
                            className={`ay-px-3 ay-py-2 ay-cursor-pointer ay-rounded-md ay-my-1 ay-transition-colors ay-duration-200 ${
                              selectedFamily === family
                                ? "ay-bg-gray-100 ay-text-black"
                                : "ay-hover:ay-bg-gray-50"
                            }`}
                          >
                            {family}
                          </div>
                        ))
                      ) : (
                        <div className="ay-px-3 ay-py-2 ay-text-gray-500">
                          {translations.noFamiliesInCategory}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="ay-px-4 ay-py-3 ay-text-gray-500 ay-text-center">
                {translations.noCategories}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FamilySelector
